export interface ChatWidgetConfig {
  version?: string;
  apiUrl: string;
  /**
   * Which chat backend to talk to.
   *
   *   'sales-bot' — POST /api/sales-bot/query/stream  (default)
   *   'chat'      — POST /api/chat/query/stream       (legacy)
   *
   * Both are supported: the two send different event shapes, and the client
   * reads either, so switching is a config change with no code change. Set it
   * to 'chat' to fall back if a deployment is still on the old pipeline.
   */
  chatEndpoint?: 'sales-bot' | 'chat';
  userId?: string;
  teamId?: string;

  theme?: {
    primaryColor?: string;
    mode?: 'light' | 'dark' | 'auto';
  };

  branding?: {
    companyName?: string;
    showPoweredBy?: boolean;
    poweredByText?: string;
    poweredByUrl?: string;
  };

  behavior?: {
    autoOpen?: boolean;
    defaultState?: 'collapsed' | 'preview' | 'full';
    position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
    mobileFullscreen?: boolean;
    enableSound?: boolean;
  };

  messages?: {
    welcomeMessage?: string;
    placeholderText?: string;
    errorMessage?: string;
    offlineMessage?: string;
  };

  suggestions?: {
    enabled?: boolean;
    fallbackQuestions?: string[];
  };

  customization?: {
    css?: string;
    className?: string;
  };

  callbacks?: {
    onOpen?: () => void;
    onClose?: () => void;
  };

  /**
   * Advisor context: 'general' (default) or 'prediction-market'.
   * When not provided, automatically detected from page URL / pathname.
   */
  context?: 'general' | 'prediction-market' | string;
}

export interface ChatMessage {
  type: 'user' | 'assistant';
  text: string;
  timestamp?: number;
  followups?: string[];
}

export const DEFAULT_CONFIG: Partial<ChatWidgetConfig> = {
  version: '1.0.0',
  chatEndpoint: 'sales-bot',
  theme: {
    primaryColor: '#6720ff',
    mode: 'auto'
  },
  branding: {
    companyName: 'Vinfotech Product Expert',
    showPoweredBy: true,
    poweredByText: 'Usually replies instantly',
    poweredByUrl: undefined
  },
  behavior: {
    autoOpen: false,
    defaultState: 'collapsed',
    position: 'bottom-right',
    mobileFullscreen: true,
    enableSound: false
  },
  messages: {
    welcomeMessage: 'How can I help you today?',
    placeholderText: 'Ask a question...',
    errorMessage: "I apologize, but I'm having trouble connecting right now. Please try again.",
    offlineMessage: 'You appear to be offline. Please check your connection.'
  },
  suggestions: {
    enabled: true,
    fallbackQuestions: [
      'What services do you offer?',
      'How can you help my business?',
      'Tell me about your solutions'
    ]
  }
};

/**
 * Narrows the dedicated advisor to one page of the site.
 *
 * Built on the server from a Keystatic "Advisor Links" entry. The advisor
 * presents itself as that product and sends `path` to the chat backend as the
 * page the conversation is about — the same signal the chat widget sends when
 * it runs on that page.
 */
export interface AdvisorPageScope {
  /** The page's public address, e.g. /prizepicks-clone-development. */
  path: string;
  /** Shown as the badge above the heading and in the browser tab. */
  name: string;
  /** Shown under the heading. May be empty. */
  intro: string;
  /** Opening questions: the editor's, else the backend's for this page. May be empty. */
  suggestions: string[];
}

